import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { NAV_LINKS, CONTACT } from "@/data/content";
import { scrollToId } from "@/lib/scroll";

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      data-testid="site-header"
      initial={{ y: -80 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 ${
        scrolled ? "bg-cream/80 backdrop-blur-xl border-b border-ink/5" : "bg-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8 h-16 md:h-20 flex items-center justify-between">
        <button
          onClick={() => scrollToId("#top")}
          data-testid="logo-link"
          className="font-display text-xl md:text-2xl font-black tracking-tighter lowercase"
        >
          isavicente<span className="text-green">.</span>
        </button>

        <nav className="hidden md:flex items-center gap-8">
          {NAV_LINKS.map((l) => (
            <button
              key={l.href}
              onClick={() => scrollToId(l.href)}
              data-testid={`nav-${l.href.replace("#", "")}`}
              className="text-sm font-medium text-ink/70 hover:text-ink relative group transition-colors"
            >
              {l.label}
              <span className="absolute -bottom-1 left-0 h-[2px] w-0 bg-green group-hover:w-full transition-all duration-300" />
            </button>
          ))}
          <Link
            to="/ofertaultimotrimestre"
            data-testid="nav-oferta"
            className="text-sm font-bold text-green hover:text-ink transition-colors"
          >
            Oferta 🔥
          </Link>
        </nav>

        <button
          onClick={() => scrollToId("#contacto")}
          data-testid="header-cta"
          className="hidden md:inline-flex items-center rounded-full bg-mango hover:bg-mango-hover text-ink font-bold text-sm px-5 py-2.5 transition-colors duration-300"
        >
          Hablemos
        </button>

        <button
          data-testid="mobile-menu-toggle"
          onClick={() => setOpen((v) => !v)}
          className="md:hidden p-2 -mr-2"
          aria-label="Abrir menú"
        >
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="md:hidden overflow-hidden bg-cream border-b border-ink/10"
          >
            <div className="px-5 py-6 flex flex-col gap-5">
              {NAV_LINKS.map((l) => (
                <button
                  key={l.href}
                  onClick={() => {
                    setOpen(false);
                    setTimeout(() => scrollToId(l.href), 80);
                  }}
                  className="font-display text-2xl font-bold tracking-tight text-left"
                >
                  {l.label}
                </button>
              ))}
              <Link
                to="/ofertaultimotrimestre"
                onClick={() => setOpen(false)}
                className="font-display text-2xl font-bold tracking-tight text-green text-left"
              >
                Oferta 🔥
              </Link>
              <a
                href={CONTACT.whatsapp}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-2 inline-flex justify-center rounded-full bg-mango text-ink font-bold px-5 py-3"
              >
                Hablemos
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
